import { useEffect, useState } from "react";

export type CartLine = {
  productId: string;
  name: string;
  unitPrice: number;
  originalPrice: number;
  discountedUnitPrice?: number | null;
  promotionMinQuantity?: number | null;
  discountPercentage?: number | null;
  promotionName?: string | null;
  quantity: number;
};

export type CartScope = { mode: "qr"; tableSlug: string } | { mode: "delivery" };

export type CartState = {
  orgSlug: string;
  scope?: CartScope;
  lines: CartLine[];
};

const STORAGE_PREFIX = "beztack:cart:";

function key(orgSlug: string, scope?: CartScope) {
  if (!scope) {
    return `${STORAGE_PREFIX}${orgSlug}`;
  }
  if (scope.mode === "qr") {
    return `${STORAGE_PREFIX}${orgSlug}:qr:${scope.tableSlug}`;
  }
  return `${STORAGE_PREFIX}${orgSlug}:delivery`;
}

function scopesEqual(a: CartScope | undefined, b: CartScope | undefined): boolean {
  if (!a && !b) {
    return true;
  }
  if (!(a && b)) {
    return false;
  }
  if (a.mode !== b.mode) {
    return false;
  }
  if (a.mode === "qr" && b.mode === "qr") {
    return a.tableSlug === b.tableSlug;
  }
  return true;
}

export function readCart(orgSlug: string, scope?: CartScope): CartState {
  if (typeof window === "undefined") {
    return { orgSlug, scope, lines: [] };
  }
  try {
    const raw = window.localStorage.getItem(key(orgSlug, scope));
    if (!raw) {
      return { orgSlug, scope, lines: [] };
    }
    const parsed = JSON.parse(raw) as CartState;
    if (
      parsed.orgSlug === orgSlug &&
      Array.isArray(parsed.lines) &&
      scopesEqual(parsed.scope, scope)
    ) {
      return parsed;
    }
  } catch {
    // ignore
  }
  return { orgSlug, scope, lines: [] };
}

export function writeCart(state: CartState) {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.setItem(key(state.orgSlug, state.scope), JSON.stringify(state));
}

export function clearCart(orgSlug: string, scope?: CartScope) {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.removeItem(key(orgSlug, scope));
}

function persistedDiscountedUnitPrice(line: CartLine): number | null {
  if (typeof line.discountedUnitPrice === "number") {
    return line.discountedUnitPrice;
  }

  return line.unitPrice < line.originalPrice ? line.unitPrice : null;
}

export function lineUnitPrice(line: CartLine) {
  const discountedUnitPrice = persistedDiscountedUnitPrice(line);

  if (discountedUnitPrice === null) {
    return line.originalPrice;
  }

  if (line.promotionMinQuantity && line.quantity < line.promotionMinQuantity) {
    return line.originalPrice;
  }

  return discountedUnitPrice;
}

export function lineSubtotal(line: CartLine) {
  return lineUnitPrice(line) * line.quantity;
}

export function cartSubtotal(state: CartState) {
  return state.lines.reduce((sum, line) => sum + lineSubtotal(line), 0);
}

export function cartItemCount(state: CartState) {
  return state.lines.reduce((sum, line) => sum + line.quantity, 0);
}

const STORAGE_EVENT = "beztack:cart:changed";

type CartChangeDetail = {
  orgSlug: string;
  scopeKey: string;
};

function scopeKey(scope?: CartScope): string {
  if (!scope) {
    return "";
  }
  if (scope.mode === "qr") {
    return `qr:${scope.tableSlug}`;
  }
  return "delivery";
}

function broadcastCartChange(orgSlug: string, scope?: CartScope) {
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(
    new CustomEvent<CartChangeDetail>(STORAGE_EVENT, {
      detail: { orgSlug, scopeKey: scopeKey(scope) },
    }),
  );
}

export function useCart(orgSlug: string | undefined, scope?: CartScope) {
  const [state, setState] = useState<CartState>(() =>
    orgSlug ? readCart(orgSlug, scope) : { orgSlug: "", scope, lines: [] },
  );

  const currentScopeKey = scopeKey(scope);

  useEffect(() => {
    if (!orgSlug) {
      return;
    }
    setState(readCart(orgSlug, scope));
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<CartChangeDetail>).detail;
      if (detail?.orgSlug === orgSlug && detail.scopeKey === currentScopeKey) {
        setState(readCart(orgSlug, scope));
      }
    };
    window.addEventListener(STORAGE_EVENT, handler as EventListener);
    return () => {
      window.removeEventListener(STORAGE_EVENT, handler as EventListener);
    };
    // scope is captured by currentScopeKey in deps below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgSlug, currentScopeKey]);

  const update = (next: CartState) => {
    writeCart(next);
    broadcastCartChange(next.orgSlug, next.scope);
    setState(next);
  };

  const addLine = (line: Omit<CartLine, "quantity"> & { quantity?: number }) => {
    if (!orgSlug) {
      return;
    }
    const current = readCart(orgSlug, scope);
    const existing = current.lines.find((l) => l.productId === line.productId);
    const quantity = line.quantity ?? 1;
    const lines = existing
      ? current.lines.map((l) =>
          l.productId === line.productId
            ? {
                ...l,
                name: line.name,
                unitPrice: line.unitPrice,
                originalPrice: line.originalPrice,
                discountedUnitPrice: line.discountedUnitPrice,
                promotionMinQuantity: line.promotionMinQuantity,
                discountPercentage: line.discountPercentage,
                promotionName: line.promotionName,
                quantity: l.quantity + quantity,
              }
            : l,
        )
      : [
          ...current.lines,
          {
            productId: line.productId,
            name: line.name,
            unitPrice: line.unitPrice,
            originalPrice: line.originalPrice,
            discountedUnitPrice: line.discountedUnitPrice,
            promotionMinQuantity: line.promotionMinQuantity,
            discountPercentage: line.discountPercentage,
            promotionName: line.promotionName,
            quantity,
          },
        ];
    update({ orgSlug, scope, lines });
  };

  const setQuantity = (productId: string, quantity: number) => {
    if (!orgSlug) {
      return;
    }
    const current = readCart(orgSlug, scope);
    const lines =
      quantity <= 0
        ? current.lines.filter((l) => l.productId !== productId)
        : current.lines.map((l) => (l.productId === productId ? { ...l, quantity } : l));
    update({ orgSlug, scope, lines });
  };

  const removeLine = (productId: string) => {
    setQuantity(productId, 0);
  };

  const clear = () => {
    if (!orgSlug) {
      return;
    }
    clearCart(orgSlug, scope);
    broadcastCartChange(orgSlug, scope);
    setState({ orgSlug, scope, lines: [] });
  };

  return {
    state,
    addLine,
    setQuantity,
    removeLine,
    clear,
    subtotal: cartSubtotal(state),
    itemCount: cartItemCount(state),
  };
}
